import { BadgeCheck, FileCheck2, KeyRound, MailCheck, Save, UserRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '../../auth/useAuth';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { UserAvatar } from '../../components/UserAvatar';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import type { TalentProfile } from '../../types/domain';

function formatCnic(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 13);
  if (digits.length <= 5) return digits;
  if (digits.length <= 12) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`;
}

export function ProfilePage() {
  const {
    accessToken,
    acceptRequiredConsents,
    changePassword,
    memberships,
    profile,
    resendEmailVerification,
    updateMarketingConsent,
    updateProfile,
  } = useAuth();
  const talent = useApiCollection<TalentProfile>(
    'personal-talent-identity',
    '/talent/?mine=true',
    accessToken,
    Boolean(profile?.isBrandAmbassador),
  );
  const talentProfile = talent.data?.results.find((item) => item.user_id === profile?.id);
  const identityLocked = talentProfile?.verification_status === 'pending'
    || talentProfile?.verification_status === 'suspended';
  const accountLocked = profile?.isBrandAmbassador
    && profile.baVerificationStatus === 'suspended';
  const [fullName, setFullName] = useState(profile?.fullName ?? '');
  const [phone, setPhone] = useState(profile?.phone ?? '');
  const [cnic, setCnic] = useState(profile?.cnic ?? '');
  const [dateOfBirth, setDateOfBirth] = useState(profile?.dateOfBirth ?? '');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [verificationSending, setVerificationSending] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(
    profile?.consents.marketing.accepted ?? false,
  );
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [consentSaved, setConsentSaved] = useState(false);
  const [deletionConfirming, setDeletionConfirming] = useState(false);
  const [deletionRequesting, setDeletionRequesting] = useState(false);
  const [deletionError, setDeletionError] = useState<string | null>(null);
  const [deletionNotice, setDeletionNotice] = useState<string | null>(null);

  useEffect(() => () => {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
  }, [avatarPreview]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    const normalizedFullName = fullName.trim();
    const normalizedPhone = phone.trim();
    const normalizedCnic = cnic.trim();
    const normalizedDateOfBirth = dateOfBirth.trim();
    const result = await updateProfile({
      fullName: normalizedFullName,
      phone: normalizedPhone || null,
      ...(profile?.isBrandAmbassador ? {
        cnic: normalizedCnic || null,
        dateOfBirth: normalizedDateOfBirth || null,
      } : {}),
      ...(avatarFile ? { avatar: avatarFile } : {}),
    });
    setSaving(false);
    if (result) {
      setError(result);
      return;
    }
    setFullName(normalizedFullName);
    setPhone(normalizedPhone);
    setCnic(normalizedCnic);
    setDateOfBirth(normalizedDateOfBirth);
    setAvatarFile(null);
    setAvatarPreview(null);
    setSaved(true);
  }

  function selectAvatar(file: File | null) {
    if (file && file.size > 2 * 1024 * 1024) {
      setError('Profile pictures must be 2 MB or smaller.');
      setAvatarFile(null);
      setAvatarPreview(null);
      return;
    }
    setError(null);
    setAvatarFile(file);
    setAvatarPreview(file ? URL.createObjectURL(file) : null);
  }

  async function submitPassword(event: FormEvent) {
    event.preventDefault();
    setPasswordError(null);
    setPasswordSaved(false);
    if (newPassword !== passwordConfirmation) {
      setPasswordError('New passwords do not match.');
      return;
    }
    setPasswordSaving(true);
    const result = await changePassword({ currentPassword, newPassword });
    setPasswordSaving(false);
    if (result) {
      setPasswordError(result);
      return;
    }
    setCurrentPassword('');
    setNewPassword('');
    setPasswordConfirmation('');
    setPasswordSaved(true);
  }

  async function resendVerification() {
    setVerificationSending(true);
    setVerificationError(null);
    setVerificationSent(false);
    const result = await resendEmailVerification();
    setVerificationSending(false);
    if (result) {
      setVerificationError(result);
      return;
    }
    setVerificationSent(true);
  }

  async function acceptPolicies() {
    if (!profile) return;
    if (!termsChecked || !privacyChecked) {
      setConsentError('Accept both current required policies to continue.');
      return;
    }
    setConsentSaving(true);
    setConsentError(null);
    setConsentSaved(false);
    const result = await acceptRequiredConsents({
      termsVersion: profile.consents.terms.version,
      privacyVersion: profile.consents.privacy.version,
    });
    setConsentSaving(false);
    if (result) {
      setConsentError(result);
      return;
    }
    setTermsChecked(false);
    setPrivacyChecked(false);
    setConsentSaved(true);
  }

  async function saveMarketingPreference() {
    if (!profile) return;
    setConsentSaving(true);
    setConsentError(null);
    setConsentSaved(false);
    const result = await updateMarketingConsent(
      marketingConsent,
      profile.consents.marketing.version,
    );
    setConsentSaving(false);
    if (result) {
      setConsentError(result);
      return;
    }
    setConsentSaved(true);
  }

  async function requestAccountDeletion() {
    if (!accessToken) {
      setDeletionError('Your session is no longer available. Please sign in again.');
      return;
    }
    setDeletionRequesting(true);
    setDeletionError(null);
    setDeletionNotice(null);
    try {
      const response = await apiRequest<{ detail: string }>('/auth/account-deletion-requests/', {
        accessToken,
        method: 'POST',
        body: JSON.stringify({ privacy_acknowledged: true }),
      });
      setDeletionConfirming(false);
      setDeletionNotice(response.detail);
    } catch (cause) {
      setDeletionError(getApiErrorMessage(cause));
    } finally {
      setDeletionRequesting(false);
    }
  }

  return (
    <>
      <header className="page-heading">
        <div>
          <p className="eyebrow">Personal account</p>
          <h1>My profile</h1>
          <p>Update the personal details shown across your Brand Field Crew workspaces.</p>
        </div>
        <span className="record-count"><UserRound size={16} /> Account</span>
      </header>

      <div className="profile-layout">
        <div className="profile-settings-column">
          <section className="content-card management-panel">
            <div className="card-heading">
              <div><h2>Personal details</h2><p>Your email remains the secure sign-in identifier.</p></div>
            </div>
            <form className="management-form" onSubmit={submit}>
              <div className="profile-picture-field">
                <UserAvatar
                  className="profile-picture-preview"
                  imageAlt={profile?.avatar || avatarPreview ? `${profile?.fullName ?? 'User'} profile picture` : ''}
                  name={fullName || profile?.fullName || 'User'}
                  src={avatarPreview ?? profile?.avatar}
                />
                <div>
                  <strong>Display picture</strong>
                  <input
                    accept="image/*"
                    aria-label="Choose display picture"
                    className="file-picker-input sr-only"
                    id="profile-avatar"
                    disabled={identityLocked || accountLocked}
                    onChange={(event) => selectAvatar(event.target.files?.[0] ?? null)}
                    type="file"
                  />
                  <label className="file-picker-button" htmlFor="profile-avatar">Choose picture</label>
                  <small>{avatarFile?.name ?? 'JPG, PNG or WebP, up to 2 MB.'}</small>
                </div>
              </div>
              <div className="form-grid profile-form-grid">
                <label>Full name<input disabled={accountLocked} required value={fullName} onChange={(event) => setFullName(event.target.value)} /></label>
                <label>Phone number<input disabled={accountLocked} placeholder="Optional" type="tel" value={phone} onChange={(event) => setPhone(event.target.value)} /></label>
                {profile?.isBrandAmbassador ? (
                  <>
                    <label>
                      CNIC
                      <input
                        autoComplete="off"
                        inputMode="numeric"
                        maxLength={15}
                        disabled={identityLocked || accountLocked}
                        onChange={(event) => setCnic(formatCnic(event.target.value))}
                        pattern="[0-9]{5}-[0-9]{7}-[0-9]"
                        placeholder="12345-1234567-1"
                        required
                        value={cnic}
                      />
                    </label>
                    <label>
                      Date of birth (optional)
                      <input
                        disabled={identityLocked || accountLocked}
                        onChange={(event) => setDateOfBirth(event.target.value)}
                        type="date"
                        value={dateOfBirth}
                      />
                    </label>
                  </>
                ) : null}
                <label className="wide-field">Email<input readOnly type="email" value={profile?.email ?? ''} /></label>
              </div>
              <p className="field-help">{profile?.isBrandAmbassador ? 'CNIC must contain 13 digits. CNIC and date of birth are protected as sensitive identity data. ' : ''}{identityLocked || accountLocked ? 'Profile changes are locked during review or suspension. ' : ''}Contact an administrator if your sign-in email needs to change.</p>
              {profile?.isBrandAmbassador ? (
                <div className={`email-verification-status ${profile.emailVerified ? 'verified' : 'pending'}`}>
                  <span><MailCheck size={18} /><strong>{profile.emailVerified ? 'Email verified' : 'Email verification required'}</strong></span>
                  {!profile.emailVerified ? (
                    <button className="button secondary small" disabled={verificationSending} onClick={() => { void resendVerification(); }} type="button">
                      {verificationSending ? <InlineSpinner /> : <MailCheck size={15} />} {verificationSending ? 'Sending…' : 'Resend verification email'}
                    </button>
                  ) : null}
                </div>
              ) : null}
              {verificationError ? <p className="form-error" role="alert">{verificationError}</p> : null}
              {verificationSent ? <p className="form-success" role="status"><MailCheck size={16} /> Verification email sent. Check your inbox.</p> : null}
              {error ? <p className="form-error" role="alert">{error}</p> : null}
              {saved ? <p className="form-success" role="status"><BadgeCheck size={16} /> Profile updated.</p> : null}
              <div className="form-actions"><button className="button primary" disabled={saving || accountLocked} type="submit">{saving ? <InlineSpinner /> : <Save size={16} />} {saving ? 'Saving…' : 'Save profile'}</button></div>
            </form>
          </section>

          {profile ? (
            <section className="content-card management-panel">
              <div className="card-heading">
                <div><h2>Privacy and consent</h2><p>Review required policies and control optional communication.</p></div>
                <FileCheck2 size={20} />
              </div>
              <div className="consent-status-list">
                <div>
                  <span><strong>Terms of Service</strong><small>Version {profile.consents.terms.version}</small></span>
                  <a href={profile.consents.terms.url} rel="noreferrer" target="_blank">Read terms</a>
                  <span className={`status-pill ${profile.consents.terms.accepted ? 'status-active' : 'status-pending_review'}`}>{profile.consents.terms.accepted ? 'Accepted' : 'Required'}</span>
                </div>
                <div>
                  <span><strong>Privacy Notice</strong><small>Version {profile.consents.privacy.version}</small></span>
                  <a href={profile.consents.privacy.url} rel="noreferrer" target="_blank">Read notice</a>
                  <span className={`status-pill ${profile.consents.privacy.accepted ? 'status-active' : 'status-pending_review'}`}>{profile.consents.privacy.accepted ? 'Accepted' : 'Required'}</span>
                </div>
              </div>
              {!profile.consents.terms.accepted || !profile.consents.privacy.accepted ? (
                <div className="required-consent-box">
                  <label className="consent-option"><input checked={termsChecked} onChange={(event) => setTermsChecked(event.target.checked)} type="checkbox" /><span>I accept the current Terms of Service.</span></label>
                  <label className="consent-option"><input checked={privacyChecked} onChange={(event) => setPrivacyChecked(event.target.checked)} type="checkbox" /><span>I have read and accept the current Privacy Notice.</span></label>
                  <button className="button primary small" disabled={consentSaving || !termsChecked || !privacyChecked} onClick={() => { void acceptPolicies(); }} type="button">{consentSaving ? <InlineSpinner /> : <FileCheck2 size={15} />} Accept required policies</button>
                </div>
              ) : null}
              <div className="marketing-consent-box">
                <label className="consent-option"><input checked={marketingConsent} onChange={(event) => setMarketingConsent(event.target.checked)} type="checkbox" /><span>Receive optional BFC product news and opportunities. This does not affect account or operational email.</span></label>
                <button className="button secondary small" disabled={consentSaving || marketingConsent === profile.consents.marketing.accepted} onClick={() => { void saveMarketingPreference(); }} type="button">Save communication preference</button>
              </div>
              {consentError ? <p className="form-error" role="alert">{consentError}</p> : null}
              {consentSaved ? <p className="form-success" role="status"><BadgeCheck size={16} /> Consent preference recorded.</p> : null}
            </section>
          ) : null}

          <section className="content-card management-panel">
            <div className="card-heading">
              <div><h2>Account and data</h2><p>Request deletion of your BFC account and associated personal data.</p></div>
            </div>
            <p className="field-help">BFC verifies every request and reviews security, audit, legal, dispute, and campaign-record retention before completing it. Submitting a request does not immediately deactivate your account.</p>
            {!deletionConfirming ? (
              <div className="form-actions">
                <button className="button danger-outline" onClick={() => { setDeletionConfirming(true); setDeletionError(null); setDeletionNotice(null); }} type="button">Request account deletion</button>
              </div>
            ) : (
              <div className="required-consent-box" role="alert">
                <p><strong>Confirm account-deletion request</strong></p>
                <p>We will send a one-time verification link to <strong>{profile?.email}</strong>. By continuing, you acknowledge the current <a href={profile?.consents.privacy.url} rel="noreferrer" target="_blank">Privacy Notice</a>.</p>
                <div className="form-actions">
                  <button className="button secondary" disabled={deletionRequesting} onClick={() => setDeletionConfirming(false)} type="button">Cancel</button>
                  <button className="button danger" disabled={deletionRequesting} onClick={() => { void requestAccountDeletion(); }} type="button">{deletionRequesting ? <InlineSpinner /> : null} {deletionRequesting ? 'Sending…' : 'Send verification link'}</button>
                </div>
              </div>
            )}
            {deletionError ? <p className="form-error" role="alert">{deletionError}</p> : null}
            {deletionNotice ? <p className="form-success" role="status"><MailCheck size={16} /> {deletionNotice}</p> : null}
          </section>

          <section className="content-card management-panel">
            <div className="card-heading">
              <div><h2>Change password</h2><p>Confirm your current password before choosing a new one.</p></div>
            </div>
            <form className="management-form" onSubmit={(event) => { void submitPassword(event); }}>
              <div className="form-grid password-form-grid">
                <label className="wide-field">
                  Current password
                  <input autoComplete="current-password" required type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} />
                </label>
                <label>
                  New password
                  <input autoComplete="new-password" minLength={8} required type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} />
                </label>
                <label>
                  Confirm new password
                  <input autoComplete="new-password" minLength={8} required type="password" value={passwordConfirmation} onChange={(event) => setPasswordConfirmation(event.target.value)} />
                </label>
              </div>
              <p className="field-help">Use at least 8 characters and avoid common or entirely numeric passwords.</p>
              {passwordError ? <p className="form-error" role="alert">{passwordError}</p> : null}
              {passwordSaved ? <p className="form-success" role="status"><BadgeCheck size={16} /> Password changed securely.</p> : null}
              <div className="form-actions">
                <button className="button primary" disabled={passwordSaving} type="submit">
                  {passwordSaving ? <InlineSpinner /> : <KeyRound size={16} />} {passwordSaving ? 'Changing…' : 'Change password'}
                </button>
              </div>
            </form>
          </section>
        </div>

        <section className="content-card membership-card">
          <div className="card-heading"><div><h2>Workspace access</h2><p>Roles are managed by authorized administrators.</p></div></div>
          {memberships.length ? (
            <div className="compact-list profile-memberships">
              {memberships.map((membership) => (
                <div key={membership.id}>
                  <span className="avatar small">{membership.organizationName.slice(0, 1)}</span>
                  <span><strong>{membership.organizationName}</strong><small>{membership.organizationType}</small></span>
                  <span className="status-pill status-active">{membership.role.replaceAll('_', ' ')}</span>
                </div>
              ))}
            </div>
          ) : <p className="profile-empty-access">This account has no organisation memberships.</p>}
        </section>
      </div>
    </>
  );
}
